/**
 * "Scrivi a piu' persone": chi riceve e chi viene escluso, con il motivo.
 *
 * Regole (28/09/2026):
 *  - numero valido (8-15 cifre, normalizzato come il resto della chat);
 *  - niente gruppi;
 *  - fuori chi ha scritto STOP (clients.ai_processing_opt_out_source =
 *    'stop_keyword'): e' l'unico segnale "non contattarmi" che il CRM registra
 *    oggi per WhatsApp;
 *  - niente doppioni (stesso numero una volta sola);
 *  - Numero Standard (WAHA): solo numeri con WhatsApp noto (una chat
 *    esistente nell'organizzazione o il campo "Numero WhatsApp" compilato) e
 *    solo chi e' Lead/Cliente dell'organizzazione o ha gia' una chat con quel
 *    numero. Scrivere a sconosciuti da un numero normale e' la via piu' breve
 *    per farselo bloccare;
 *  - Numero Ufficiale (Cloud): basta un numero valido, perche' si inviano solo
 *    modelli approvati e Meta rifiuta da sola i numeri senza WhatsApp, senza
 *    rischi per il numero;
 *  - tetto per invio (50 Standard, 250 Ufficiale): gli altri restano fuori.
 *
 * Modulo puro, testato in __tests__/bulkRecipients.test.ts.
 */
import { normalizeWhatsAppDigits } from '../phoneDigits';
import { BULK_LIMITS, type BulkChannel } from './bulkSchedule';

export interface BulkCandidate {
  clientId: string | null;
  businessName?: string | null;
  contactName?: string | null;
  entityType?: string | null;
  phone?: string | null;
  whatsappNumber?: string | null;
  /** Chat gia' esistente (arrivo da etichetta chat). */
  chatJid?: string | null;
  /** Nome della chat, se non c'e' un'anagrafica. */
  chatName?: string | null;
  optOut?: boolean;
}

export interface BulkRecipient {
  digits: string;
  chatJid: string;
  /** Nome completo da mostrare. */
  name: string;
  /** Valore di {{nome}}. */
  firstName: string;
  clientId: string | null;
}

export type ExclusionReason =
  | 'senza_numero'
  | 'gruppo'
  | 'non_contattare'
  | 'doppione'
  | 'whatsapp_non_noto'
  | 'fuori_perimetro'
  | 'oltre_tetto';

export const EXCLUSION_LABELS: Record<ExclusionReason, string> = {
  senza_numero: 'Senza un numero valido',
  gruppo: 'Gruppi (non si scrive ai gruppi)',
  non_contattare: 'Hanno chiesto di non essere contattati (STOP)',
  doppione: 'Numero gia\' presente nella lista',
  whatsapp_non_noto: 'WhatsApp non noto (nessuna chat e nessun numero WhatsApp in scheda)',
  fuori_perimetro: 'Non sono Lead o Clienti e non hanno una chat con questo numero',
  oltre_tetto: 'Oltre il tetto di questo invio',
};

export interface KnownChats {
  /** Numeri con una chat su qualsiasi numero aziendale dell'organizzazione. */
  inOrg: Set<string>;
  /** Numeri con una chat sul numero da cui si invia. */
  onConfig: Set<string>;
}

export interface BuildResult {
  recipients: BulkRecipient[];
  excluded: Array<{ name: string; reason: ExclusionReason }>;
  summary: Partial<Record<ExclusionReason, number>>;
}

const firstToken = (s: string) => s.trim().split(/\s+/)[0] ?? '';

/**
 * Valore di {{nome}}, stessa regola dei flussi: il referente (contact_name) e'
 * sempre una persona e si prende il primo nome; la ragione sociale si taglia
 * solo per le persone fisiche, per le aziende resta intera.
 */
export function firstNameOf(c: Pick<BulkCandidate, 'contactName' | 'businessName' | 'entityType' | 'chatName'>): string {
  const contact = (c.contactName || '').trim();
  if (contact) return firstToken(contact);
  const business = (c.businessName || '').trim();
  if (business) return c.entityType === 'company' ? business : firstToken(business);
  const chat = (c.chatName || '').trim();
  return chat && !/^\+?\d[\d\s]*$/.test(chat) ? firstToken(chat) : '';
}

function displayName(c: BulkCandidate, digits: string): string {
  return (c.businessName || c.contactName || c.chatName || '').trim() || (digits ? `+${digits}` : 'Senza nome');
}

function digitsOf(c: BulkCandidate): string {
  const raw = c.whatsappNumber || c.phone || (c.chatJid && c.chatJid.endsWith('@c.us') ? c.chatJid : '');
  const d = normalizeWhatsAppDigits(raw || '').replace(/\D/g, '');
  return d.length >= 8 && d.length <= 15 ? d : '';
}

export function buildRecipients(candidates: BulkCandidate[], channel: BulkChannel, known: KnownChats): BuildResult {
  const recipients: BulkRecipient[] = [];
  const excluded: BuildResult['excluded'] = [];
  const seen = new Set<string>();
  const max = BULK_LIMITS[channel].max;
  const exclude = (c: BulkCandidate, digits: string, reason: ExclusionReason) =>
    excluded.push({ name: displayName(c, digits), reason });

  for (const c of candidates) {
    if (c.chatJid && c.chatJid.endsWith('@g.us')) { exclude(c, '', 'gruppo'); continue; }
    const digits = digitsOf(c);
    if (!digits) { exclude(c, '', 'senza_numero'); continue; }
    if (c.optOut) { exclude(c, digits, 'non_contattare'); continue; }
    if (seen.has(digits)) { exclude(c, digits, 'doppione'); continue; }
    if (channel === 'standard') {
      const hasChatHere = known.onConfig.has(digits);
      const whatsappKnown = hasChatHere || known.inOrg.has(digits) || !!(c.whatsappNumber || '').trim();
      if (!whatsappKnown) { exclude(c, digits, 'whatsapp_non_noto'); continue; }
      if (!c.clientId && !hasChatHere) { exclude(c, digits, 'fuori_perimetro'); continue; }
    }
    seen.add(digits);
    if (recipients.length >= max) { exclude(c, digits, 'oltre_tetto'); continue; }
    recipients.push({
      digits,
      chatJid: `${digits}@c.us`,
      name: displayName(c, digits),
      firstName: firstNameOf(c),
      clientId: c.clientId,
    });
  }

  const summary: BuildResult['summary'] = {};
  for (const e of excluded) summary[e.reason] = (summary[e.reason] ?? 0) + 1;
  return { recipients, excluded, summary };
}
